/**
 * AccountSection — the personal half of /settings (PROD-27).
 *
 * Composes IdentityCard ("My profile" — name / pronouns / phone /
 * language), SecurityCard (email + sign-in methods), and
 * NotificationsCard (topic × channel toggles), all formerly on the
 * retired /profile page. Everything about you-the-person lives here,
 * reachable for every persona via the sidebar Settings item.
 *
 * Data flow mirrors the old /profile page: `useMeProfileQuery`
 * hydrates name fields + email + saved notification preference rows
 * in one round-trip, and the user's resolved nav (from `useProfile()`)
 * drives the notification matrix filter so e.g. the Billing topic only
 * renders when Billing is in nav.
 */

import { Skeleton } from '@ordo/ui/skeleton'
import { useMemo } from 'react'

import { useMeProfileQuery } from '@/api/me'
import { IdentityCard } from '@/components/settings/identity-card'
import { NotificationsCard } from '@/components/settings/notifications-card'
import { SecurityCard } from '@/components/settings/security-card'
import { StudentSchoolCard } from '@/components/settings/student-school-card'
import { useProfile } from '@/stores/profile'

export function AccountSection() {
  const { data, isPending, error } = useMeProfileQuery()
  const { profile } = useProfile()

  const visibleNavSlugs = useMemo(() => {
    const slugs = new Set<string>()
    for (const item of profile?.nav.items ?? []) {
      slugs.add(item.slug)
    }
    return slugs
  }, [profile])

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const me = data?.me ?? null

  if (error && !me) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm font-medium text-destructive">Couldn&apos;t load your account settings.</p>
        <p className="mt-1 text-xs text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!me) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      <IdentityCard me={me} />
      {profile?.profileCodes.includes('STUDENT') ? <StudentSchoolCard /> : null}
      <SecurityCard email={me.email} />
      <NotificationsCard preferences={me.notificationPreferences} visibleNavSlugs={visibleNavSlugs} />
    </div>
  )
}
